/**
 * One-shot backfill of the historical raw spool (`events.jsonl`) into lean
 * session summaries.
 *
 * A real machine accumulated 148MB / 231,575 lines / 3,606 sessions here that
 * never left disk. This module distills that in a single streaming pass
 * (O(sessions) memory) into the same summary shape the live hook posts, so
 * historical and live records reconcile on `session_id`.
 *
 * SAFETY: distillation is read-only and is the default. Posting requires an
 * explicit opt-in, and truncating the user's spool requires a second explicit
 * opt-in AND a fully successful post. Nothing here runs on its own — it is
 * reachable only via `orgx-wizard hooks backfill`.
 */

import { createReadStream, renameSync, statSync, writeFileSync } from "node:fs";
import { basename } from "node:path";
import { createInterface } from "node:readline";

import { SESSION_SUMMARY_SCHEMA_VERSION } from "./session-summary-hook.js";
import {
  SESSION_SUMMARY_MAX_NAMED_TOOL_KEYS,
  SESSION_WORK_EPISODE_MAX_TOOL_KEYS,
} from "./session-summary-contract.js";
import { normalizeSessionWorkContext } from "./session-work-context.js";

export interface SessionSummary {
  schema_version: string;
  source: string;
  session_id: string;
  source_client: string;
  repo: string;
  cwd: string | null;
  day: string;
  started_at: string | null;
  ended_at: string | null;
  duration_min: number;
  events: number;
  prompts: number;
  tool_calls: number;
  tools: Record<string, number>;
  edits: number;
  bash: number;
  metadata?: Record<string, unknown>;
}

export interface MinimalCloudSummaryMetadata {
  captureId?: string;
  captureKind:
    | "turn_boundary"
    | "run_end"
    | "session_end"
    | "historical_backfill"
    | "legacy_unspecified";
  queuedAt?: string;
}

const SESSION_EXECUTION_OBSERVATION_VERSION =
  "orgx-session-execution-observation/v1";
const SESSION_EXECUTION_MAX_ACTIONS = 32;
const SESSION_COMPLETED_ACTION_AGGREGATE_VERSION =
  "orgx-session-completed-action-aggregate/v1";
const SESSION_COMPLETED_ACTION_STATUSES = [
  "succeeded",
  "failed",
  "completed_unknown",
] as const;
const SESSION_COMPLETED_ACTION_EFFECTS = [
  "inspect",
  "change",
  "execute",
  "network",
  "invoke",
  "unspecified",
] as const;
const SESSION_EXECUTION_UNAVAILABLE_FIELDS = new Set([
  "intent",
  "authority_decision",
  "artifact_refs",
  "cost",
  "verification",
]);
const SESSION_EXECUTION_PERMISSION_MODES = new Set([
  "default",
  "acceptEdits",
  "plan",
  "dontAsk",
  "bypassPermissions",
]);
const SESSION_WORK_EPISODE_VERSION = "orgx-session-work-episode/v1";
const SESSION_WORK_EPISODE_MAX_INTENTS = 12;
const SESSION_WORK_EPISODE_UNAVAILABLE_FIELDS = new Set([
  "intent",
  "acceptance",
  "decisions",
  "artifacts",
  "verification",
  "outcome",
  "cost",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function boundedString(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, maxLength) : undefined;
}

function boundedCount(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? Math.min(value, 10_000_000)
    : 0;
}

function boundedToolCounts(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter(([name]) => name.length > 0 && name.length <= 80)
      .slice(0, SESSION_WORK_EPISODE_MAX_TOOL_KEYS)
      .map(([name, observed]) => [name, boundedCount(observed)]),
  );
}

function validTimestamp(value: unknown): string | undefined {
  const text = boundedString(value, 100);
  return text && !Number.isNaN(Date.parse(text)) ? text : undefined;
}

function normalizeObservedAction(value: unknown): Record<string, unknown> | null {
  if (!isRecord(value)) return null;
  const id = boundedString(value.id, 80);
  const toolName = boundedString(value.tool_name, 48);
  const status = boundedString(value.status, 40);
  if (
    !id ||
    !toolName ||
    !status ||
    !["running", "succeeded", "failed", "completed_unknown"].includes(status)
  ) {
    return null;
  }

  const startedAt = validTimestamp(value.started_at);
  const completedAt = validTimestamp(value.completed_at);
  const durationMs =
    typeof value.duration_ms === "number" &&
    Number.isInteger(value.duration_ms) &&
    value.duration_ms >= 0
      ? Math.min(value.duration_ms, 86_400_000)
      : undefined;
  const turnId = boundedString(value.turn_id, 120);
  const effect = boundedString(value.effect, 40);
  const target = boundedString(value.target, 240);
  return {
    id,
    type: "tool.invoke",
    tool_name: toolName,
    status,
    provenance: "runtime_observed",
    ...(startedAt ? { started_at: startedAt } : {}),
    ...(completedAt ? { completed_at: completedAt } : {}),
    ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
    ...(turnId ? { turn_id: turnId } : {}),
    ...(effect ? { effect } : {}),
    ...(target ? { target } : {}),
  };
}

function normalizeAggregateCounts(
  value: unknown,
  keys: readonly string[],
): Record<string, number> | null {
  if (!isRecord(value)) return null;
  const counts: Record<string, number> = {};
  for (const key of keys) {
    if (
      typeof value[key] !== "number" ||
      !Number.isInteger(value[key]) ||
      value[key] < 0 ||
      value[key] > 10_000_000
    ) {
      return null;
    }
    counts[key] = value[key];
  }
  return counts;
}

function normalizeCompletedActionAggregate(
  value: unknown,
  context: {
    observedCompleted: number;
    detailsIncluded: number;
    detailsOmitted: number;
  },
): Record<string, unknown> | null {
  if (
    !isRecord(value) ||
    value.schema_version !== SESSION_COMPLETED_ACTION_AGGREGATE_VERSION
  ) {
    return null;
  }
  const actionsAggregated = boundedCount(value.actions_aggregated);
  const actionsUnaggregated = boundedCount(value.actions_unaggregated);
  const detailsIncluded = boundedCount(value.details_included);
  const detailsOmitted = boundedCount(value.details_omitted);
  const detailMode = boundedString(value.detail_mode, 40);
  const detailStrategy = boundedString(value.detail_strategy, 40);
  const statusCounts = normalizeAggregateCounts(
    value.status_counts,
    SESSION_COMPLETED_ACTION_STATUSES,
  );
  const effectCounts = normalizeAggregateCounts(
    value.effect_counts,
    SESSION_COMPLETED_ACTION_EFFECTS,
  );
  const digest = isRecord(value.digest) ? value.digest : null;
  const digestValue = boundedString(digest?.value, 80);
  if (
    !statusCounts ||
    !effectCounts ||
    value.actions_completed_observed !== context.observedCompleted ||
    detailsIncluded !== context.detailsIncluded ||
    detailsOmitted !== context.detailsOmitted ||
    value.detail_limit !== SESSION_EXECUTION_MAX_ACTIONS ||
    detailMode !== (detailsOmitted > 0 ? "representative" : "complete") ||
    detailStrategy !== (detailsOmitted > 0 ? "first_8_last_24" : "all") ||
    digest?.algorithm !== "sha256-chain-v1" ||
    !digestValue ||
    !/^sha256:[0-9a-f]{64}$/.test(digestValue)
  ) {
    return null;
  }
  const aggregateCountMatchesObserved =
    actionsAggregated + actionsUnaggregated === context.observedCompleted;
  const detailPartitionMatchesObserved =
    detailsIncluded + detailsOmitted === context.observedCompleted;
  const statusCountsMatchAggregate =
    Object.values(statusCounts).reduce((sum, count) => sum + count, 0) ===
    actionsAggregated;
  const effectCountsMatchAggregate =
    Object.values(effectCounts).reduce((sum, count) => sum + count, 0) ===
    actionsAggregated;
  const captureComplete =
    actionsUnaggregated === 0 &&
    aggregateCountMatchesObserved &&
    detailPartitionMatchesObserved &&
    statusCountsMatchAggregate &&
    effectCountsMatchAggregate;
  const firstCompletedAt = validTimestamp(value.first_completed_at);
  const lastCompletedAt = validTimestamp(value.last_completed_at);
  if (
    (actionsAggregated > 0 && (!firstCompletedAt || !lastCompletedAt)) ||
    (firstCompletedAt &&
      lastCompletedAt &&
      Date.parse(lastCompletedAt) < Date.parse(firstCompletedAt))
  ) {
    return null;
  }
  return {
    schema_version: SESSION_COMPLETED_ACTION_AGGREGATE_VERSION,
    capture_complete: captureComplete,
    actions_completed_observed: context.observedCompleted,
    actions_aggregated: actionsAggregated,
    actions_unaggregated: actionsUnaggregated,
    details_included: detailsIncluded,
    details_omitted: detailsOmitted,
    detail_mode: detailMode,
    detail_strategy: detailStrategy,
    detail_limit: SESSION_EXECUTION_MAX_ACTIONS,
    status_counts: statusCounts,
    effect_counts: effectCounts,
    ...(firstCompletedAt ? { first_completed_at: firstCompletedAt } : {}),
    ...(lastCompletedAt ? { last_completed_at: lastCompletedAt } : {}),
    digest: {
      algorithm: "sha256-chain-v1",
      value: digestValue,
    },
    consistency: {
      aggregate_count_matches_observed: aggregateCountMatchesObserved,
      detail_partition_matches_observed: detailPartitionMatchesObserved,
      status_counts_match_aggregate: statusCountsMatchAggregate,
      effect_counts_match_aggregate: effectCountsMatchAggregate,
    },
  };
}

/**
 * Allowlist the action observation that can leave the machine. The generated
 * hook never records prompts, tool arguments/results, paths, or error text;
 * this second boundary also strips any hostile fields injected into a local
 * state file before delivery.
 */
export function normalizeSessionExecutionObservation(
  value: unknown,
): Record<string, unknown> | null {
  if (!isRecord(value) || value.schema_version !== SESSION_EXECUTION_OBSERVATION_VERSION) {
    return null;
  }
  const boundary = boundedString(value.boundary, 40);
  if (
    !boundary ||
    ![
      "turn_boundary",
      "run_end",
      "session_end",
      "historical_backfill",
      "legacy_unspecified",
    ].includes(boundary)
  ) {
    return null;
  }
  const actions = (Array.isArray(value.actions) ? value.actions : [])
    .slice(0, SESSION_EXECUTION_MAX_ACTIONS)
    .map(normalizeObservedAction)
    .filter((action): action is Record<string, unknown> => action !== null);
  const unavailableFields = (Array.isArray(value.unavailable_fields)
    ? value.unavailable_fields
    : []
  ).filter(
    (field): field is string =>
      typeof field === "string" && SESSION_EXECUTION_UNAVAILABLE_FIELDS.has(field),
  );
  const permissionModes = (Array.isArray(value.permission_modes_observed)
    ? value.permission_modes_observed
    : []
  ).filter(
    (mode): mode is string =>
      typeof mode === "string" && SESSION_EXECUTION_PERMISSION_MODES.has(mode),
  );
  const actionsCompletedObserved = boundedCount(value.actions_completed_observed);
  const actionsOmitted = boundedCount(value.actions_omitted);
  const completedDetailsIncluded = actions.filter(
    (action) => action.status !== "running",
  ).length;
  const completedActionAggregate = normalizeCompletedActionAggregate(
    value.completed_action_aggregate,
    {
      observedCompleted: actionsCompletedObserved,
      detailsIncluded: completedDetailsIncluded,
      detailsOmitted: actionsOmitted,
    },
  );
  const actionCaptureComplete =
    value.action_capture_complete === true &&
    (actionsOmitted === 0 || completedActionAggregate?.capture_complete === true);

  return {
    schema_version: SESSION_EXECUTION_OBSERVATION_VERSION,
    boundary,
    terminal_observed: value.terminal_observed === true,
    actions,
    ...(completedActionAggregate
      ? { completed_action_aggregate: completedActionAggregate }
      : {}),
    actions_completed_observed: actionsCompletedObserved,
    actions_omitted: actionsOmitted,
    start_times_omitted: boundedCount(value.start_times_omitted),
    pending_actions: boundedCount(value.pending_actions),
    permission_requests_observed: boundedCount(value.permission_requests_observed),
    permission_modes_observed: [...new Set(permissionModes)].slice(0, 8),
    // A bounded detail set is complete only when its aggregate survived the
    // allowlist and reconciled. Otherwise the cloud payload must fail closed
    // instead of asking the delivery queue to wait for an impossible receipt.
    action_capture_complete: actionCaptureComplete,
    unavailable_fields: [...new Set(unavailableFields)],
  };
}

/**
 * Admit the bounded semantic episode separately from strict receipt context.
 * This surface may include redacted user-request excerpts when the user opted
 * into bounded work capture, so its privacy declaration is preserved exactly.
 */
export function normalizeSessionWorkEpisode(
  value: unknown,
): Record<string, unknown> | null {
  if (!isRecord(value) || value.schema_version !== SESSION_WORK_EPISODE_VERSION) {
    return null;
  }
  const episodeId = boundedString(value.episode_id, 500);
  const sourceClient = boundedString(value.source_client, 120);
  const sourceSessionId = boundedString(value.source_session_id, 500);
  const status = boundedString(value.status, 40);
  const boundary = boundedString(value.boundary, 40);
  const startedAt = validTimestamp(value.started_at);
  const updatedAt = validTimestamp(value.updated_at);
  if (
    !episodeId ||
    !sourceClient ||
    !sourceSessionId ||
    !status ||
    !["active", "ended"].includes(status) ||
    !boundary ||
    !["turn_boundary", "run_end", "session_end", "legacy_unspecified"].includes(boundary) ||
    !startedAt ||
    !updatedAt
  ) {
    return null;
  }

  const intent = isRecord(value.intent) ? value.intent : {};
  const observations = (Array.isArray(intent.observations) ? intent.observations : [])
    .slice(0, SESSION_WORK_EPISODE_MAX_INTENTS)
    .map((candidate) => {
      if (!isRecord(candidate)) return null;
      const ordinal = boundedCount(candidate.ordinal);
      const observedAt = validTimestamp(candidate.observed_at);
      const excerpt = boundedString(candidate.text_excerpt, 600);
      const digest = boundedString(candidate.text_sha256, 64);
      if (!ordinal || !observedAt || !excerpt || !digest || !/^[a-f0-9]{64}$/.test(digest)) {
        return null;
      }
      return {
        ordinal,
        observed_at: observedAt,
        text_excerpt: excerpt,
        text_sha256: digest,
        truncated: candidate.truncated === true,
        provenance: "user_asserted",
      };
    })
    .filter((candidate) => candidate !== null);
  const progress = isRecord(value.progress) ? value.progress : {};
  const privacy = isRecord(value.privacy) ? value.privacy : {};
  const lineage = isRecord(value.lineage) ? value.lineage : {};
  const rootSessionId = boundedString(lineage.root_session_id, 200);
  const parentSessionId = boundedString(lineage.parent_session_id, 200);
  const resumedFromSessionId = boundedString(
    lineage.resumed_from_session_id,
    200,
  );
  const unavailableFields = (Array.isArray(value.unavailable_fields)
    ? value.unavailable_fields
    : []
  ).filter(
    (field): field is string =>
      typeof field === "string" && SESSION_WORK_EPISODE_UNAVAILABLE_FIELDS.has(field),
  );

  return {
    schema_version: SESSION_WORK_EPISODE_VERSION,
    episode_id: episodeId,
    source_client: sourceClient,
    source_session_id: sourceSessionId,
    session_kind: value.session_kind === "user_session" ? "user_session" : "unknown",
    status,
    boundary,
    started_at: startedAt,
    updated_at: updatedAt,
    ...(rootSessionId || parentSessionId || resumedFromSessionId
      ? {
          lineage: {
            ...(rootSessionId ? { root_session_id: rootSessionId } : {}),
            ...(parentSessionId ? { parent_session_id: parentSessionId } : {}),
            ...(resumedFromSessionId
              ? { resumed_from_session_id: resumedFromSessionId }
              : {}),
            provenance: "runtime_observed",
          },
        }
      : {}),
    intent: {
      observations,
      observations_omitted: boundedCount(intent.observations_omitted),
      capture_complete: intent.capture_complete === true,
    },
    progress: {
      events_observed: boundedCount(progress.events_observed),
      prompts_observed: boundedCount(progress.prompts_observed),
      actions_completed_observed: boundedCount(progress.actions_completed_observed),
      edits_observed: boundedCount(progress.edits_observed),
      shell_actions_observed: boundedCount(progress.shell_actions_observed),
      tools_observed: boundedToolCounts(progress.tools_observed),
    },
    provenance: {
      identity: "runtime_observed",
      intent: observations.length > 0 ? "user_asserted" : "unavailable",
      progress: "runtime_observed",
    },
    unavailable_fields: [...new Set(unavailableFields)],
    privacy: {
      profile: observations.length > 0 ? "bounded_work_episode" : "metadata_only",
      content_included: observations.length > 0,
      max_intent_excerpt_chars: 600,
    },
  };
}

function normalizeSessionSummaryCompaction(
  value: unknown,
): Record<string, unknown> | null {
  if (
    !isRecord(value) ||
    value.schema_version !== "orgx-session-summary-compaction/v1"
  ) {
    return null;
  }
  return {
    schema_version: "orgx-session-summary-compaction/v1",
    original_bytes: boundedCount(value.original_bytes),
    cap_bytes: boundedCount(value.cap_bytes),
    action_observations_omitted: boundedCount(
      value.action_observations_omitted,
    ),
    intent_observations_omitted: boundedCount(
      value.intent_observations_omitted,
    ),
    work_contexts_omitted: boundedCount(value.work_contexts_omitted),
    cwd_values_omitted: boundedCount(value.cwd_values_omitted),
    lineage_values_omitted: boundedCount(value.lineage_values_omitted),
    work_episodes_omitted: boundedCount(value.work_episodes_omitted),
    execution_observations_omitted: boundedCount(
      value.execution_observations_omitted,
    ),
  };
}

/**
 * Build the summary that may leave the machine under the default minimal
 * profile. The local state and preview may retain `cwd` for repository
 * detection, but a full filesystem path is neither required for attribution
 * nor permitted by the default cloud collection policy.
 */
export function buildMinimalCloudSummary(
  summary: SessionSummary,
  metadata: MinimalCloudSummaryMetadata,
): Omit<SessionSummary, "cwd"> {
  const execution = normalizeSessionExecutionObservation(summary.metadata?.execution);
  const workContext = normalizeSessionWorkContext(summary.metadata?.work_context);
  const workEpisode = normalizeSessionWorkEpisode(summary.metadata?.work_episode);
  const compaction = normalizeSessionSummaryCompaction(
    summary.metadata?.compaction,
  );
  const { cwd: _localOnlyCwd, metadata: _localOnlyMetadata, ...minimal } = summary;
  return {
    ...minimal,
    metadata: {
      capture: {
        ...(metadata.captureId ? { id: metadata.captureId } : {}),
        kind: metadata.captureKind,
        ...(metadata.queuedAt ? { queued_at: metadata.queuedAt } : {}),
      },
      privacy: {
        profile: workEpisode?.privacy && isRecord(workEpisode.privacy) && workEpisode.privacy.content_included === true
          ? "bounded_work_episode"
          : "minimal",
        raw_content_included:
          Boolean(workEpisode?.privacy && isRecord(workEpisode.privacy) && workEpisode.privacy.content_included === true),
        cwd_included: false,
      },
      ...(execution ? { execution } : {}),
      ...(workContext ? { work_context: workContext } : {}),
      ...(workEpisode ? { work_episode: workEpisode } : {}),
      ...(compaction ? { compaction } : {}),
    },
  };
}

export interface SpoolDistillResult {
  spoolPath: string;
  spoolBytes: number;
  lines: number;
  badLines: number;
  summaries: SessionSummary[];
  bytes: { total: number; max: number; p50: number; p90: number; mean: number };
}

interface MutableSessionState {
  session_id: string;
  source_client: string;
  repo: string;
  cwd: string | null;
  firstTs: number | null;
  lastTs: number | null;
  events: number;
  prompts: number;
  tool_calls: number;
  tools: Record<string, number>;
  edits: number;
  bash: number;
}

const MAX_REPORTED_TOOLS = SESSION_SUMMARY_MAX_NAMED_TOOL_KEYS;
const MAX_TOOL_NAME = 48;

/** Mirrors the generated hook's `repoOf` so live and historical records agree. */
export function repoOf(cwd: string | null | undefined): string {
  if (!cwd) return "unknown";
  const marker = "/Code/";
  const at = cwd.indexOf(marker);
  if (at >= 0) {
    const rest = cwd.slice(at + marker.length);
    const slash = rest.indexOf("/");
    const name = slash < 0 ? rest : rest.slice(0, slash);
    if (name) return name;
  }
  return basename(cwd) || "unknown";
}

/** Mirrors the generated hook's `normalizeToolName`. */
export function normalizeToolName(name: string): string {
  const mcp = /^mcp__[0-9a-fA-F-]{16,}__(.+)$/.exec(name);
  const base = mcp ? `mcp__${mcp[1]}` : name;
  return base.length > MAX_TOOL_NAME ? base.slice(0, MAX_TOOL_NAME) : base;
}

/** Mirrors the generated hook's `boundTools`. */
export function boundTools(tools: Record<string, number>): Record<string, number> {
  const merged: Record<string, number> = {};
  for (const [name, count] of Object.entries(tools)) {
    const key = normalizeToolName(name);
    merged[key] = (merged[key] ?? 0) + count;
  }
  const entries = Object.entries(merged).sort((a, b) => b[1] - a[1]);
  if (entries.length <= MAX_REPORTED_TOOLS) return Object.fromEntries(entries);

  const out = Object.fromEntries(entries.slice(0, MAX_REPORTED_TOOLS));
  const tail = entries.slice(MAX_REPORTED_TOOLS);
  out.__other__ = tail.reduce((sum, [, count]) => sum + count, 0);
  out.__other_tools__ = tail.length;
  return out;
}

function toSummary(state: MutableSessionState): SessionSummary {
  const { firstTs, lastTs } = state;
  return {
    schema_version: SESSION_SUMMARY_SCHEMA_VERSION,
    source: "orgx_session_summary",
    session_id: state.session_id,
    source_client: state.source_client,
    repo: state.repo,
    cwd: state.cwd,
    day: firstTs === null ? "unknown" : new Date(firstTs).toISOString().slice(0, 10),
    started_at: firstTs === null ? null : new Date(firstTs).toISOString(),
    ended_at: lastTs === null ? null : new Date(lastTs).toISOString(),
    duration_min: firstTs !== null && lastTs !== null ? Math.round((lastTs - firstTs) / 60_000) : 0,
    events: state.events,
    prompts: state.prompts,
    tool_calls: state.tool_calls,
    tools: boundTools(state.tools),
    edits: state.edits,
    bash: state.bash,
  };
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] ?? 0;
}

/**
 * Stream the spool into summaries. Read-only: never mutates the spool.
 */
export async function distillSpool(spoolPath: string): Promise<SpoolDistillResult> {
  const sessions = new Map<string, MutableSessionState>();
  let lines = 0;
  let badLines = 0;

  const reader = createInterface({
    input: createReadStream(spoolPath),
    crlfDelay: Infinity,
  });

  for await (const line of reader) {
    lines += 1;
    if (!line.trim()) continue;

    let record: Record<string, unknown>;
    try {
      record = JSON.parse(line) as Record<string, unknown>;
    } catch {
      badLines += 1;
      continue;
    }

    const sessionId = typeof record.session_id === "string" ? record.session_id : "unknown";
    const cwd = typeof record.cwd === "string" ? record.cwd : null;
    const timestamp = typeof record.timestamp === "string" ? Date.parse(record.timestamp) : NaN;
    const ts = Number.isFinite(timestamp) ? timestamp : null;

    let state = sessions.get(sessionId);
    if (!state) {
      state = {
        session_id: sessionId,
        source_client: typeof record.source_client === "string" ? record.source_client : "unknown",
        repo: repoOf(cwd),
        cwd,
        firstTs: ts,
        lastTs: ts,
        events: 0,
        prompts: 0,
        tool_calls: 0,
        tools: {},
        edits: 0,
        bash: 0,
      };
      sessions.set(sessionId, state);
    }

    state.events += 1;
    if (ts !== null) {
      if (state.firstTs === null || ts < state.firstTs) state.firstTs = ts;
      if (state.lastTs === null || ts > state.lastTs) state.lastTs = ts;
    }
    if (cwd && state.cwd === null) {
      state.cwd = cwd;
      state.repo = repoOf(cwd);
    }
    if (typeof record.source_client === "string" && state.source_client === "unknown") {
      state.source_client = record.source_client;
    }

    const event = typeof record.event === "string" ? record.event : "unknown";
    if (event === "UserPromptSubmit" || event.includes("user_prompt")) state.prompts += 1;

    const summary = record.summary as { tool_name?: unknown } | undefined;
    const tool = summary && typeof summary.tool_name === "string" ? summary.tool_name : null;
    if (tool && (event === "PostToolUse" || event.includes("post_tool_use"))) {
      state.tool_calls += 1;
      state.tools[tool] = (state.tools[tool] ?? 0) + 1;
      if (tool === "Write" || tool === "Edit" || tool === "NotebookEdit") state.edits += 1;
      if (tool === "Bash") state.bash += 1;
    }
  }

  const summaries = [...sessions.values()]
    .map(toSummary)
    .sort((a, b) => (a.started_at ?? "").localeCompare(b.started_at ?? ""));

  const sizes = summaries
    .map((summary) => Buffer.byteLength(JSON.stringify(summary), "utf8"))
    .sort((a, b) => a - b);
  const total = sizes.reduce((sum, value) => sum + value, 0);

  let spoolBytes = 0;
  try {
    spoolBytes = statSync(spoolPath).size;
  } catch {
    spoolBytes = 0;
  }

  return {
    spoolPath,
    spoolBytes,
    lines,
    badLines,
    summaries,
    bytes: {
      total,
      max: sizes.at(-1) ?? 0,
      p50: percentile(sizes, 0.5),
      p90: percentile(sizes, 0.9),
      mean: sizes.length > 0 ? Math.round(total / sizes.length) : 0,
    },
  };
}

export interface BackfillPostResult {
  attempted: number;
  posted: number;
  failed: number;
  firstError?: string;
}

export type BackfillPoster = (summary: SessionSummary) => Promise<{ ok: boolean; status: number }>;

/**
 * Post distilled summaries one at a time. Stops early after a run of failures so
 * a dead endpoint does not produce thousands of doomed requests.
 */
export async function postSummaries(
  summaries: readonly SessionSummary[],
  post: BackfillPoster,
  options: { maxConsecutiveFailures?: number } = {},
): Promise<BackfillPostResult> {
  const maxConsecutiveFailures = options.maxConsecutiveFailures ?? 5;
  const result: BackfillPostResult = { attempted: 0, posted: 0, failed: 0 };
  let consecutiveFailures = 0;

  for (const summary of summaries) {
    result.attempted += 1;
    const response = await post(summary);
    if (response.ok) {
      result.posted += 1;
      consecutiveFailures = 0;
      continue;
    }

    result.failed += 1;
    consecutiveFailures += 1;
    result.firstError ??= `HTTP ${response.status} for session ${summary.session_id}`;
    if (consecutiveFailures >= maxConsecutiveFailures) break;
  }

  return result;
}

export interface TruncateResult {
  truncated: boolean;
  reason?: string;
  archivePath?: string;
  bytesReleased: number;
}

/**
 * Truncate the spool after a fully successful backfill.
 *
 * Refuses unless every distilled session posted. The old spool is moved to a
 * `.backfilled-<timestamp>` archive rather than deleted, so this is reversible:
 * removing the archive stays the user's decision.
 */
export function truncateSpool(
  spoolPath: string,
  post: BackfillPostResult,
  options: { now?: Date; force?: boolean } = {},
): TruncateResult {
  if (!options.force && (post.failed > 0 || post.attempted === 0)) {
    return {
      truncated: false,
      reason:
        post.attempted === 0
          ? "nothing was posted, so there is nothing safe to truncate"
          : `${post.failed} session(s) failed to post`,
      bytesReleased: 0,
    };
  }

  let bytesReleased = 0;
  try {
    bytesReleased = statSync(spoolPath).size;
  } catch {
    return { truncated: false, reason: "spool not found", bytesReleased: 0 };
  }

  const stamp = (options.now ?? new Date()).toISOString().replace(/[:.]/g, "-");
  const archivePath = `${spoolPath}.backfilled-${stamp}`;
  renameSync(spoolPath, archivePath);
  writeFileSync(spoolPath, "", { encoding: "utf8", mode: 0o600 });

  return { truncated: true, archivePath, bytesReleased };
}
